import { flow, types } from 'mobx-state-tree';
import {
  getDigiMagazines,
  getSingleDigiMagazine,
  addSingleDigiMagazine,
  updateSingleDigiMagazine,
  deleteSingleDigiMagazine,
} from '../services/digiMagazineApi';
import { composeError } from '../utils/transforms';
import { DigiMagazineResponse, DigiMagazine, TargetGroup, DigiMagazineFile, DigiMagazinePayload } from '../types';
import { withRootStore } from './withRootStore';

const DigiMagazineFileModel = types.model('DigiMagazineFileModel', {
  id: types.number,
  name: types.string,
  image: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  published: types.optional(types.boolean, false),
  order: types.number,
  magazineNumber: types.maybeNull(types.string),
  releaseDate: types.maybeNull(types.string),
  releaseYear: types.maybeNull(types.number),
  src: types.maybeNull(types.string),
  state: types.optional(types.string, ''),
});

const DigiMagazineModel = types.model('DigiMagazineModel', {
  id: types.optional(types.number, -1),
  name: types.string,
  image: types.maybeNull(types.string),
  description: types.string,
  published: types.optional(types.boolean, false),
  publisher: types.string,
  mediaTypeId: types.optional(types.number, -1),
  churchId: types.maybeNull(types.string),
  communityId: types.maybeNull(types.number),
  targetGroupIds: types.optional(types.array(types.number), []),
  files: types.optional(types.array(DigiMagazineFileModel), []),
});

export const DigiMagazineStore = types
  .model({
    digiMagazines: types.optional(types.array(DigiMagazineModel), []),
    selectedDigiMagazine: types.maybeNull(DigiMagazineModel),
    loading: false,
  })
  .extend(withRootStore)
  .views((self) => ({
    get itemsArray() {
      return self.digiMagazines.slice();
    },
  }))
  .actions((self) => {
    const getAllDigiMagazines = flow(function* () {
      try {
        self.loading = true;
        const response = yield getDigiMagazines();
        self.digiMagazines = response.map((digiMagazine: DigiMagazineResponse) => ({
          ...digiMagazine,
        }));
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
        console.error(error);
      } finally {
        self.loading = false;
      }
    });

    const getSelectedDigiMagazine = flow(function* (id: string) {
      try {
        const digiMagazineDataFromApi = yield getSingleDigiMagazine(id);

        if (digiMagazineDataFromApi) {
          self.selectedDigiMagazine = {
            ...digiMagazineDataFromApi,
            mediaTypeId: digiMagazineDataFromApi.mediaType?.id,
            communityId: digiMagazineDataFromApi.community?.id,
            targetGroupIds: digiMagazineDataFromApi.targetGroups?.map((data: TargetGroup) => data.id),
            files: digiMagazineDataFromApi.files
              ? digiMagazineDataFromApi.files.map((s: DigiMagazineFile) => ({ ...s, state: '' }))
              : [],
          };
          // selectedDigiMagazine has been updated
        } else {
          console.warn('%%%% STORE: getSelectedDigiMagazine - No data received from API or other issue.');
        }
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
        console.error(error);
      }
    });

    const createDigiMagazine = flow(function* (
      digiMagazine: DigiMagazine,
      digiMagazineFilesForPayload: DigiMagazineFile[],
      filesToUploadForApi: File[],
      parentCoverImageFile?: File
    ) {
      self.loading = true;
      try {
        const payloadForApi: DigiMagazinePayload = {
          ...digiMagazine,
          coverImageFile: parentCoverImageFile,
          files: digiMagazineFilesForPayload,
        };
        const responseData = yield addSingleDigiMagazine(payloadForApi, filesToUploadForApi);
        self.rootStore.errorStore.setSuccess('save_success');
        self.loading = false;
        return responseData?.id;
      } catch (err) {
        console.error(err);
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.loading = false;
      }
    });

    const updateDigiMagazine = flow(function* (
      digiMagazine: DigiMagazine,
      digiMagazineFilesForPayload: DigiMagazineFile[],
      filesToUploadForApi: File[],
      parentCoverImageFile?: File
    ) {
      self.loading = true;
      // update with provided changes
      try {
        if (digiMagazine.id == null || Number(digiMagazine.id) <= 0) {
          console.warn('%%%% STORE: updateDigiMagazine - Missing or invalid digiMagazine.id; aborting update.');
          self.rootStore.errorStore.setError('invalid_update_id');
          self.loading = false;
          return;
        }
        const payloadForApi: DigiMagazinePayload = {
          ...digiMagazine,
          coverImageFile: parentCoverImageFile,
          files: digiMagazineFilesForPayload,
        };
        const digiMagazinePayloadFromApi = yield updateSingleDigiMagazine(
          String(digiMagazine.id),
          payloadForApi,
          filesToUploadForApi
        );
        // Success: backend returned updated parent

        if (digiMagazinePayloadFromApi && digiMagazine.id != null) {
          yield getSelectedDigiMagazine(String(digiMagazine.id));
        } else if (!digiMagazine.id) {
          console.warn('%%%% STORE: Cannot re-fetch DigiMagazine after update because ID is missing or invalid.');
        } else {
          console.warn('%%%% STORE: No API response data received from updateSingleDigiMagazine, not re-fetching.');
        }

        self.rootStore.errorStore.setSuccess('save_success');
        self.loading = false;
        return self.selectedDigiMagazine;
      } catch (err) {
        console.error(err);
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.loading = false;
      }
    });

    const deleteDigiMagazine = flow(function* (id: string) {
      try {
        yield deleteSingleDigiMagazine(id);
        self.rootStore.errorStore.setSuccess('delete_success');
      } catch (err) {
        console.error(err);
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
      }
    });

    return {
      getAllDigiMagazines,
      getSelectedDigiMagazine,
      createDigiMagazine,
      updateDigiMagazine,
      deleteDigiMagazine,
    };
  });
